import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Loader2,
  AlertTriangle,
  User,
  Clock,
  MapPin,
  Banknote,
  Coffee,
  Play,
  Monitor,
  CheckCircle,
  XCircle,
  Globe,
  Smartphone,
  HelpCircle,
  MonitorUp,
  Headphones,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useRegisters, type Register } from '@/hooks/useRegisters';
import { useLocations } from '@/hooks/useLocations';
import { toast } from 'sonner';
import { useDeviceApprovalRequests, useDeviceRegistration } from '@/hooks/useDeviceRegistration';
import { DeviceApprovalDialog } from '@/components/device/DeviceApprovalDialog';
import { useRemoteSessionViewer, useSupportRequestNotifications, type SupportRequest } from '@/hooks/useRemoteSession';
import { RemoteSessionViewerModal } from '@/components/remote';

// Helper to get device type icon
function getDeviceTypeIcon(deviceType?: string) {
  switch (deviceType) {
    case 'electron':
      return <Monitor className="h-3.5 w-3.5" />;
    case 'mobile':
      return <Smartphone className="h-3.5 w-3.5" />;
    case 'web':
    default:
      return <Globe className="h-3.5 w-3.5" />;
  }
}

// Helper to format session duration
function getSessionDuration(openedAt: string): string {
  const opened = new Date(openedAt);
  const now = new Date();
  const diffMs = now.getTime() - opened.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

export default function Registers() {
  const navigate = useNavigate();
  const { locations } = useLocations();
  const [filterLocationId, setFilterLocationId] = useState<string | null>(null);
  const { registers, isLoading, createRegister, updateRegister, deleteRegister, resumePausedSession } = useRegisters(filterLocationId);

  // Device approval requests
  const { data: pendingDeviceRequests } = useDeviceApprovalRequests(filterLocationId || undefined);
  const { denyRequest } = useDeviceRegistration();
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState<any>(null);

  // Remote session management
  const {
    connectionState,
    remoteStream,
    currentSession,
    error: remoteError,
    supportRequests,
    requestSession,
    connectToSupport,
    endSession: endRemoteSession,
    sendInput,
    hasActiveSession,
  } = useRemoteSessionViewer();

  const { dismissRequest: dismissSupportNotification } = useSupportRequestNotifications(filterLocationId || undefined);
  const [remoteViewerOpen, setRemoteViewerOpen] = useState(false);

  // Close remote viewer when session ends (disconnected or no active session)
  useEffect(() => {
    if (connectionState === 'disconnected' && !hasActiveSession && remoteViewerOpen) {
      setRemoteViewerOpen(false);
    }
  }, [connectionState, hasActiveSession, remoteViewerOpen]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRegister, setSelectedRegister] = useState<Register | null>(null);
  const [registerToDelete, setRegisterToDelete] = useState<Register | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreateRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name) {
      setFormError('Register name is required');
      return;
    }

    if (!formData.location_id) {
      setFormError('Please select a location');
      return;
    }

    try {
      await createRegister.mutateAsync({
        locationId: formData.location_id,
        name: formData.name,
      });
      setCreateDialogOpen(false);
      resetForm();
      toast.success('Register created', { description: `${formData.name} has been added` });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create register';
      setFormError(errorMessage);
      toast.error('Error creating register', {
        description: errorMessage,
      });
    }
  };

  const handleEditRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedRegister || !formData.name) {
      setFormError('Register name is required');
      return;
    }

    try {
      await updateRegister.mutateAsync({
        id: selectedRegister.id,
        updates: {
          name: formData.name,
        },
      });
      setEditDialogOpen(false);
      resetForm();
      toast.success('Register updated');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update register';
      setFormError(errorMessage);
      toast.error('Error updating register', {
        description: errorMessage,
      });
    }
  };

  const openDeleteDialog = (register: Register) => {
    setRegisterToDelete(register);
    setDeleteDialogOpen(true);
  };

  const handleDeleteRegister = async () => {
    if (!registerToDelete) return;

    try {
      await deleteRegister.mutateAsync(registerToDelete.id);
      toast.success('Register deleted', { description: `${registerToDelete.name} has been removed` });
      setDeleteDialogOpen(false);
      setRegisterToDelete(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete register';
      toast.error('Error deleting register', {
        description: errorMessage,
      });
    }
  };

  const handleToggleActive = async (register: Register, isActive: boolean) => {
    if (register.current_session && !isActive) {
      toast.error('Cannot deactivate', {
        description: 'This register has an active session. Close the session first.',
      });
      return;
    }

    try {
      await updateRegister.mutateAsync({
        id: register.id,
        updates: { is_active: isActive },
      });
      toast.success(isActive ? 'Register activated' : 'Register deactivated', {
        description: `${register.name} is now ${isActive ? 'active' : 'inactive'}`,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update register status';
      toast.error('Error', {
        description: errorMessage,
      });
    }
  };

  const openEditDialog = (register: Register) => {
    setSelectedRegister(register);
    setFormData({
      name: register.name,
      location_id: register.location_id,
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location_id: '',
    });
    setFormError(null);
    setSelectedRegister(null);
  };

  // Remote session handlers
  const handleRemoteConnect = async (register: Register) => {
    if (!register.current_session) {
      toast.error('No active session', {
        description: 'Cannot connect remotely - there is no active session on this register.',
      });
      return;
    }

    try {
      await requestSession.mutateAsync({
        locationId: register.location_id,
        registerId: register.id,
        cashierUserId: register.current_session.user_id,
        sessionType: 'full_control',
      });
      setRemoteViewerOpen(true);
      toast.success('Remote session requested', {
        description: 'Waiting for the cashier to accept the connection...',
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to request remote session';
      toast.error('Error', {
        description: errorMessage,
      });
    }
  };

  const handleConnectToSupport = async (supportRequest: SupportRequest) => {
    try {
      await connectToSupport.mutateAsync({
        requestId: supportRequest.id,
        sessionType: 'full_control',
      });
      setRemoteViewerOpen(true);
      dismissSupportNotification(supportRequest.id);
      toast.success('Connecting to support request', {
        description: `Connecting to ${supportRequest.requester_name || 'cashier'}...`,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect';
      toast.error('Error', {
        description: errorMessage,
      });
    }
  };

  const handleEndRemoteSession = async () => {
    try {
      await endRemoteSession.mutateAsync('admin_ended');
      setRemoteViewerOpen(false);
      toast.success('Session ended', {
        description: 'Remote session has been disconnected.',
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to end session';
      toast.error('Error', {
        description: errorMessage,
      });
    }
  };

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;

    return date.toLocaleDateString();
  };

  // Group registers by location
  const registersByLocation = registers.reduce((acc, register) => {
    const locationName = register.location_name || 'Unknown';
    if (!acc[locationName]) {
      acc[locationName] = [];
    }
    acc[locationName].push(register);
    return acc;
  }, {} as Record<string, Register[]>);

  return (
    <TooltipProvider>
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Register Management</h2>
          <p className="text-sm text-muted-foreground">
            Manage cash registers for each location
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Location Filter */}
          <Select
            value={filterLocationId || 'all'}
            onValueChange={(value) => setFilterLocationId(value === 'all' ? null : value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Register
          </Button>
        </div>
      </div>

      {/* Pending Device Approval Requests */}
      {pendingDeviceRequests && pendingDeviceRequests.length > 0 && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Monitor className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-base">Device Approval Requests</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {pendingDeviceRequests.length} pending request{pendingDeviceRequests.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <Badge variant="destructive">{pendingDeviceRequests.length} Pending</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {pendingDeviceRequests.map((request) => {
                const requesterName = request.requester
                  ? `${request.requester.first_name || ''} ${request.requester.last_name || ''}`.trim() || 'Unknown'
                  : 'Unknown user';
                const locationName = Array.isArray(request.location)
                  ? request.location[0]?.name
                  : request.location?.name || 'Unknown location';

                return (
                  <div
                    key={request.id}
                    className="flex items-center justify-between gap-4 p-4 bg-card rounded-lg border"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                        <User className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{requesterName}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{locationName}</span>
                          <span className="text-muted-foreground/50">|</span>
                          <Clock className="h-3 w-3" />
                          <span>{getTimeAgo(request.created_at)}</span>
                        </div>
                        {request.requested_device_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Device: {request.requested_device_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                        onClick={async () => {
                          try {
                            await denyRequest.mutateAsync({ requestId: request.id });
                            toast.success('Request denied', {
                              description: `${requesterName}'s device request has been denied.`,
                            });
                          } catch {
                            toast.error('Error', {
                              description: 'Failed to deny the request.',
                            });
                          }
                        }}
                        disabled={denyRequest.isPending}
                      >
                        <XCircle className="h-4 w-4" />
                        Deny
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        className="gap-1.5 bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => setSelectedApprovalRequest(request)}
                      >
                        <CheckCircle className="h-4 w-4" />
                        Review
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Support Requests */}
      {supportRequests.length > 0 && (
        <Card className="mb-6 border-blue-500/30 bg-blue-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Headphones className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <CardTitle className="text-base">Support Requests</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {supportRequests.length} cashier{supportRequests.length !== 1 ? 's' : ''} requesting help
                  </p>
                </div>
              </div>
              <Badge className="bg-blue-500">{supportRequests.length} Pending</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {supportRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between gap-4 p-4 bg-card rounded-lg border"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{request.requester_name || 'Unknown'}</p>
                        {request.priority === 'urgent' && (
                          <Badge variant="destructive" className="text-[10px]">Urgent</Badge>
                        )}
                        {request.priority === 'high' && (
                          <Badge variant="default" className="text-[10px] bg-amber-500">High</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{request.location_name}</span>
                        {request.register_name && (
                          <>
                            <span className="text-muted-foreground/50">|</span>
                            <Monitor className="h-3 w-3" />
                            <span>{request.register_name}</span>
                          </>
                        )}
                      </div>
                      {request.message && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          "{request.message}"
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        <span>{getTimeAgo(request.created_at)}</span>
                        {request.category && (
                          <>
                            <span className="text-muted-foreground/50">|</span>
                            <span className="capitalize">{request.category}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="default"
                    className="gap-1.5 bg-blue-500 hover:bg-blue-600 shrink-0"
                    onClick={() => handleConnectToSupport(request)}
                    disabled={connectToSupport.isPending || hasActiveSession}
                  >
                    {connectToSupport.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MonitorUp className="h-4 w-4" />
                    )}
                    Connect & Help
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registers List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading registers...</span>
          </div>
        </div>
      ) : registers.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No registers yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add registers for your locations to enable cashier sessions
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Register
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(registersByLocation).map(([locationName, locationRegisters]) => (
            <div key={locationName}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-medium">{locationName}</h3>
                <Badge variant="secondary" className="text-xs">
                  {locationRegisters.length} register{locationRegisters.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {locationRegisters.map((register) => (
                  <Card key={register.id} className={cn(!register.is_active && 'opacity-60')}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center',
                              register.current_session
                                ? 'bg-emerald-500/10'
                                : register.is_active
                                ? 'bg-primary/10'
                                : 'bg-muted'
                            )}
                          >
                            <DollarSign
                              className={cn(
                                'h-5 w-5',
                                register.current_session
                                  ? 'text-emerald-500'
                                  : register.is_active
                                  ? 'text-primary'
                                  : 'text-muted-foreground'
                              )}
                            />
                          </div>
                          <div>
                            <h4 className="font-semibold">{register.name}</h4>
                            {register.current_session ? (
                              register.current_session.status === 'paused' ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="default" className="mt-1 bg-blue-500 text-xs cursor-help">
                                      On Break
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>Session paused. Can be resumed.</TooltipContent>
                                </Tooltip>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="default" className="mt-1 bg-emerald-500 text-xs cursor-help">
                                      In Use
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>A cashier has an open session.</TooltipContent>
                                </Tooltip>
                              )
                            ) : register.is_active ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="mt-1 text-xs cursor-help">
                                    Available
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Ready to use. No active session.</TooltipContent>
                              </Tooltip>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="mt-1 text-xs cursor-help">
                                    Inactive
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Disabled by admin.</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Remote Connect - only when there's an active session */}
                            {register.current_session && register.current_session.status !== 'paused' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleRemoteConnect(register)}
                                  disabled={requestSession.isPending || hasActiveSession}
                                  className="text-blue-600"
                                >
                                  <MonitorUp className="h-4 w-4 mr-2" />
                                  Remote Connect
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem onClick={() => openEditDialog(register)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(register)}
                              className="text-red-600"
                              disabled={!!register.current_session}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Current Session Info */}
                      {register.current_session && (
                        <div className={cn(
                          "space-y-2 mb-3 p-3 rounded-lg border",
                          register.current_session.status === 'paused'
                            ? "bg-blue-500/5 border-blue-500/20"
                            : "bg-emerald-500/5 border-emerald-500/20"
                        )}>
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">{register.current_session.user_name}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>Opened {getTimeAgo(register.current_session.opened_at)}</span>
                          </div>

                          {/* Paused indicator */}
                          {register.current_session.status === 'paused' && (
                            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-500/10 rounded px-2 py-1">
                              <Coffee className="h-3 w-3" />
                              <span>
                                On break
                                {register.current_session.paused_at && (
                                  <> since {getTimeAgo(register.current_session.paused_at)}</>
                                )}
                              </span>
                            </div>
                          )}

                          <div className="flex gap-2 mt-2">
                            {/* Resume button for paused sessions */}
                            {register.current_session.status === 'paused' && (
                              <Button
                                size="sm"
                                variant="default"
                                className="flex-1 gap-2 bg-blue-500 hover:bg-blue-600"
                                onClick={async () => {
                                  if (register.current_session?.id) {
                                    try {
                                      await resumePausedSession.mutateAsync(register.current_session.id);
                                      toast.success('Session resumed', {
                                        description: `${register.current_session.user_name}'s session has been resumed.`,
                                      });
                                    } catch (err) {
                                      toast.error('Error', {
                                        description: 'Failed to resume session.',
                                      });
                                    }
                                  }
                                }}
                                disabled={resumePausedSession.isPending}
                              >
                                {resumePausedSession.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                                Resume
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className={cn(
                                "gap-2",
                                register.current_session.status === 'paused' ? "flex-1" : "w-full"
                              )}
                              onClick={() => {
                                if (register.current_session?.id) {
                                  navigate(`/close-register/${register.current_session.id}`);
                                } else {
                                  toast.error('Error', {
                                    description: 'Session ID not found. Please refresh the page.',
                                  });
                                }
                              }}
                            >
                              <Banknote className="h-4 w-4" />
                              Settle
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Device Info (if linked) */}
                      {register.device && (
                        <div className="flex items-center gap-2 mb-3 p-2 bg-muted/50 rounded-lg text-sm">
                          <div className="text-muted-foreground">
                            {getDeviceTypeIcon(register.device.device_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-xs">
                              {register.device.device_name || 'Unnamed Device'}
                            </p>
                            {register.device.last_seen_at && (
                              <p className="text-xs text-muted-foreground">
                                Last seen {getTimeAgo(register.device.last_seen_at)}
                              </p>
                            )}
                          </div>
                          {register.device.is_active ? (
                            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-200">
                              Online
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px]">
                              Offline
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Current Session Duration */}
                      {register.current_session && register.current_session.status === 'open' && (
                        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>Session duration: <span className="font-medium text-foreground">{getSessionDuration(register.current_session.opened_at)}</span></span>
                        </div>
                      )}

                      {/* Status Toggle */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium">Active</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[200px]">
                              When ON, cashiers can use this register. Turn OFF for maintenance.
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Switch
                          checked={register.is_active}
                          onCheckedChange={(checked) => handleToggleActive(register, checked)}
                          disabled={!!register.current_session}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Register Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <form onSubmit={handleCreateRegister}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Add New Register</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Create a new cash register for a location
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Select
                    value={formData.location_id}
                    onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Register Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Register 1, Front Counter, Drive Thru"
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={createRegister.isPending}>
                {createRegister.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Register
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit Register Dialog */}
      <ResponsiveModal open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <form onSubmit={handleEditRegister}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Rename Register</ResponsiveModalTitle>
              <ResponsiveModalDescription>Update the register name</ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="edit_name">Register Name *</Label>
                  <Input
                    id="edit_name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={updateRegister.isPending}>
                {updateRegister.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation Dialog */}
      <ResponsiveModal
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setRegisterToDelete(null);
        }}
      >
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <ResponsiveModalTitle>Delete Register</ResponsiveModalTitle>
                <ResponsiveModalDescription>This action cannot be undone</ResponsiveModalDescription>
              </div>
            </div>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{registerToDelete?.name}</span>? This
              will permanently remove the register.
            </p>
            {registerToDelete?.current_session && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This register has an active session. Close the session before deleting.
                </p>
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setRegisterToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRegister}
              disabled={deleteRegister.isPending || !!registerToDelete?.current_session}
            >
              {deleteRegister.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete Register
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Device Approval Dialog */}
      <DeviceApprovalDialog
        open={!!selectedApprovalRequest}
        onOpenChange={(open) => !open && setSelectedApprovalRequest(null)}
        request={selectedApprovalRequest}
        onApproved={() => setSelectedApprovalRequest(null)}
        onDenied={() => setSelectedApprovalRequest(null)}
      />

      {/* Remote Session Viewer Modal */}
      <RemoteSessionViewerModal
        open={remoteViewerOpen || hasActiveSession}
        onClose={() => setRemoteViewerOpen(false)}
        session={currentSession}
        stream={remoteStream}
        connectionState={connectionState}
        error={remoteError}
        onEndSession={handleEndRemoteSession}
        onSendInput={sendInput}
        isEndingSession={endRemoteSession.isPending}
      />
    </div>
    </TooltipProvider>
  );
}
